import { useCurrentStep } from 'api/guide';
import LoadingButton from 'components/shared/LoadingButton';
import { useSCIMGuideProgress } from 'contexts/GuideProgressContext';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import {
	useGuideValidation,
	useSaveScim
} from 'contexts/SSOConfigFieldsContext';
import useToast from 'hooks/useToast';
import { isEmpty } from 'lodash';
import React, { useCallback } from 'react';
import { ToastSeverity } from 'types/toast';
import { useNavigateToJit, useStepNavigate } from 'utils/navigation';

interface ISCIMContinueButtonProps {
	disabled?: boolean;
}

const SCIMContinueButton: React.FC<ISCIMContinueButtonProps> = ({
	disabled = false
}) => {
	const { scimGuideStepNum } = useIdPConfigContext();

	const { data: currentStepData } = useCurrentStep();

	const { navigateToNextStep } = useStepNavigate();
	const { saveScimConfig } = useSaveScim();
	const { navigateToJit } = useNavigateToJit();
	const { setStepCompleted: setSCIMStepCompleted } = useSCIMGuideProgress();
	// `disabled` reflects the CURRENT step only, and the sidebar lets the admin jump straight to
	// the last step - so an error on an earlier step (a mandatory custom attribute left unmapped
	// on the mapping step) would leave Finish enabled and save anyway. Check the GUIDE-level
	// validation before saving and report it, mirroring XAAContinueButton.
	const { fieldsErrors: guideFieldsErrors, fieldsUniqueErrorMessages } =
		useGuideValidation();
	const { addToast } = useToast();

	const onClick = useCallback(async () => {
		if (currentStepData?.isLastStep && !isEmpty(guideFieldsErrors)) {
			// Surface the messages when they belong to a field the guide renders (the admin can go
			// fix them); otherwise fall back to the generic save error so Finish is never a silent
			// no-op.
			addToast({
				severity: ToastSeverity.Error,
				content: isEmpty(fieldsUniqueErrorMessages)
					? intl.tr('scimConfig.save.saveErrorMessage')
					: fieldsUniqueErrorMessages.join('\n')
			});
			return;
		}
		setSCIMStepCompleted(scimGuideStepNum!);
		if (currentStepData?.isLastStep) {
			const saved = await saveScimConfig?.();
			if (!saved) return;
			navigateToJit();
		}
		if (!currentStepData?.isLastStep) {
			navigateToNextStep();
		}
	}, [
		addToast,
		currentStepData?.isLastStep,
		fieldsUniqueErrorMessages,
		guideFieldsErrors,
		navigateToJit,
		navigateToNextStep,
		saveScimConfig,
		scimGuideStepNum,
		setSCIMStepCompleted
	]);

	return (
		<LoadingButton
			data-testid="guide-continue-button"
			variant="contained"
			disabled={disabled}
			onClick={onClick}
		>
			{intl.tr(
				currentStepData?.isLastStep
					? 'scimConfig.guide.step.finish'
					: 'general.continue'
			)}
		</LoadingButton>
	);
};

export default SCIMContinueButton;
